/* eslint-disable no-process-env  */
'use client';

import { LocalOverrideAdapter } from '@statsig/js-local-overrides';
import {
  type StatsigOptions,
  StatsigProvider,
  type StatsigUser,
  useClientBootstrapInit,
} from '@statsig/react-bindings';
import { useEnvContext } from 'next-runtime-env';
import type { PropsWithChildren } from 'react';

import logToDatadog from '@/logger/logToDatadog';

interface StatsigClientProviderProps {
  readonly user: StatsigUser;
  readonly data?: string;
  readonly optionOverrides?: Partial<StatsigOptions>;
}

export default function StatsigProviderWrapper({
  user,
  data,
  children,
  optionOverrides,
}: PropsWithChildren<StatsigClientProviderProps>) {
  const { NEXT_PUBLIC_STATSIG_CLIENT_KEY, NEXT_PUBLIC_OURA_ENV } =
    useEnvContext();
  if (!NEXT_PUBLIC_STATSIG_CLIENT_KEY) {
    throw new Error(
      'Missing NEXT_PUBLIC_STATSIG_CLIENT_KEY environment variable.',
    );
  }

  if (!NEXT_PUBLIC_OURA_ENV) {
    throw new Error('Missing NEXT_PUBLIC_OURA_ENV environment variable.');
  }

  const stableId = user.customIDs?.stableID;

  if (!stableId) {
    logToDatadog({
      level: 'warn',
      message: 'Missing stable id in StatisProviderWrapper',
      module: 'statsig',
    });
  }

  const options: StatsigOptions = {
    environment: { tier: `${NEXT_PUBLIC_OURA_ENV}` },
    networkConfig: {
      preventAllNetworkTraffic: process.env.NODE_ENV === 'test',
    },
    customUserCacheKeyFunc: (sdkKey: string, _user: StatsigUser) => {
      if (!_user.customIDs?.stableID) {
        logToDatadog({
          level: 'warn',
          message: 'Missing stable id in StatisProviderWrapper',
          module: 'statsig',
        });
        return `sdkKey:${sdkKey}`;
      }
      return `sdkKey:${sdkKey}:stableID:${_user.customIDs.stableID}`;
    },
    ...(NEXT_PUBLIC_OURA_ENV !== 'production' && {
      overrideAdapter: new LocalOverrideAdapter(NEXT_PUBLIC_STATSIG_CLIENT_KEY),
    }),
    ...optionOverrides,
  };

  const client = useClientBootstrapInit(
    NEXT_PUBLIC_STATSIG_CLIENT_KEY,
    user,
    data ?? '{}',
    options,
  );

  return <StatsigProvider client={client}>{children}</StatsigProvider>;
}
